import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Trophy } from 'lucide-react';
import { actifsMatches, juniorsTeams, getNextCheyresMatch, formatMatchDate } from '../data/interclubs';

const isCheyres = (name) => name && name.includes('Cheyres');

const MatchCard = ({ match, accent, label, linkTo, size = 'sm' }) => {
  const cheyresIsHome = isCheyres(match.home);
  const opponent = cheyresIsHome ? match.away : match.home;
  const location = cheyresIsHome ? 'Courts TC Cheyres-Châbles' : `Courts TC ${match.home}`;
  const dateLabel = `${formatMatchDate(match.date, { full: size === 'lg' })}${match.time ? ` · ${match.time}` : ''}`;

  const colorMap = {
    emerald: {
      badge: 'bg-emerald-400 text-gray-900',
      hoverBorder: 'hover:border-emerald-400/50',
      accentText: 'text-emerald-300',
      trophy: 'text-emerald-300',
    },
    orange: {
      badge: 'bg-orange-400 text-gray-900',
      hoverBorder: 'hover:border-orange-400/50',
      accentText: 'text-orange-300',
      trophy: 'text-orange-300',
    },
  };
  const c = colorMap[accent];

  if (size === 'lg') {
    return (
      <Link
        to={linkTo}
        data-testid={`next-match-${accent}`}
        className={`group block bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 ${c.hoverBorder} transition-all`}
      >
        <div className="flex items-center justify-between mb-6">
          <span className={`${c.badge} px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider`}>{label}</span>
          <Trophy className={`w-6 h-6 ${c.trophy}`} />
        </div>

        <div className="mb-4">
          <div className={`flex items-center ${c.accentText} mb-3 text-sm`}>
            <Calendar className="w-4 h-4 mr-2" />
            <span className="font-semibold">{dateLabel}</span>
          </div>
          <p className="text-white text-2xl sm:text-3xl font-bold">
            {match.home} <span className={`${c.accentText} font-normal text-xl mx-2`}>vs</span> {match.away}
          </p>
        </div>

        <div className="flex items-center text-white/60 text-sm pt-4 border-t border-white/10">
          <MapPin className="w-4 h-4 mr-2" />
          <span>{location}</span>
        </div>
      </Link>
    );
  }

  // Small card (Juniors)
  return (
    <Link
      to={linkTo}
      data-testid={`next-match-${accent}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`group block bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 hover:bg-white/10 ${c.hoverBorder} transition-all`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className={`${c.badge} px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider`}>{label}</span>
        <Trophy className={`w-4 h-4 ${c.trophy}`} />
      </div>
      <div className={`flex items-center ${c.accentText} mb-2 text-xs`}>
        <Calendar className="w-3.5 h-3.5 mr-1.5" />
        <span className="font-semibold">{dateLabel}</span>
      </div>
      <p className="text-white text-lg font-bold leading-tight mb-1">{match.home}</p>
      <p className="text-white/60 text-xs mb-1">reçoit</p>
      <p className="text-white/90 text-base font-semibold">{match.away}</p>
      <div className="flex items-center text-white/50 text-xs pt-3 mt-3 border-t border-white/10">
        <MapPin className="w-3.5 h-3.5 mr-1.5" />
        <span>{location}</span>
      </div>
    </Link>
  );
};

const NextMatchesSection = () => {
  const nextActifs = getNextCheyresMatch(actifsMatches);
  const juniorsCards = juniorsTeams
    .map((team) => ({ team, next: getNextCheyresMatch(team.matches) }))
    .filter((x) => x.next);

  // Pas de match à venir nulle part : on cache toute la section
  if (!nextActifs && juniorsCards.length === 0) return null;

  return (
    <section
      data-testid="next-matches-section"
      className="py-16 bg-gradient-to-br from-gray-900 via-green-900 to-emerald-900 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="mb-10">
          <p className="text-emerald-300 uppercase tracking-widest text-xs font-bold mb-2">À ne pas manquer</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">Prochaines rencontres</h2>
        </div>

        {nextActifs && (
          <MatchCard
            match={nextActifs}
            accent="emerald"
            label="Actifs · 35+ 3L"
            linkTo="/interclubs"
            size="lg"
          />
        )}

        {juniorsCards.length > 0 && (
          <div
            className={`grid gap-4 ${nextActifs ? 'mt-6' : ''} ${
              juniorsCards.length === 1
                ? 'md:grid-cols-1'
                : juniorsCards.length === 2
                ? 'md:grid-cols-2'
                : 'md:grid-cols-3'
            }`}
          >
            {juniorsCards.map(({ team, next }) => (
              <MatchCard
                key={team.id}
                match={next}
                accent="orange"
                label={`Juniors · ${team.label}`}
                linkTo="/interclubs-juniors"
                size="sm"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default NextMatchesSection;
